import * as React from "react";

import { cn } from "@/lib/utils";

import { type VariantProps, cva } from "class-variance-authority";

import { Info, OctagonX, ShieldCheck, Sparkles, TriangleAlert } from "../Icons";
import { BodyText, HeaderText } from "../Text";

const calloutVariants = cva(
  "relative w-full rounded-lg p-3 [&>svg+*]:pl-5 [&>svg+div]:translate-y-[-3px] [&>svg]:absolute [&>svg]:left-3 [&>svg]:top-2.75 [&>svg]:text-foreground",
  {
    variants: {
      variant: {
        info: "[&>svg]:stroke-info",
        danger: "[&>svg]:stroke-error",
        success: "[&>svg]:stroke-success",
        warning: "[&>svg]:stroke-warning",
        ai: "[&>svg]:stroke-ai",
        grayscale: "[&>svg]:stroke-tertiary",
      },
      subtle: {
        true: "bg-white border border-secondary",
        false: "border-none",
      },
    },
    compoundVariants: [
      { variant: "info", subtle: false, className: "bg-info" },
      { variant: "danger", subtle: false, className: "bg-error" },
      { variant: "success", subtle: false, className: "bg-success" },
      { variant: "warning", subtle: false, className: "bg-warning" },
      { variant: "ai", subtle: false, className: "bg-superclinical" },
      { variant: "ai", subtle: true, className: "bg-white border border-ai" },
      { variant: "grayscale", subtle: false, className: "bg-secondary" },
    ],
    defaultVariants: {
      variant: "info",
      subtle: false,
    },
  },
);

const CalloutRoot = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & VariantProps<typeof calloutVariants>
>(({ className, variant, subtle, ...props }, ref) => (
  <div
    ref={ref}
    role="complementary"
    className={cn(calloutVariants({ variant, subtle }), className)}
    {...props}
  />
));
CalloutRoot.displayName = "CalloutRoot";

const VARIANT_TEXT_COLOR_MAP: Record<
  string,
  "info" | "success" | "warning" | "error" | "superclinical" | "tertiary"
> = {
  info: "info",
  danger: "error",
  success: "success",
  warning: "warning",
  ai: "superclinical",
  grayscale: "tertiary",
};

interface CalloutTitleProps {
  variant: "info" | "danger" | "success" | "warning" | "ai" | "grayscale";
  title: string;
  status?: string;
}

const CalloutTitle: React.FC<CalloutTitleProps> = ({
  variant,
  title,
  status,
}) => (
  <div className="flex items-center gap-2 pt-[2px]">
    <HeaderText
      size="sm"
      className="leading-relaxed flex-1"
      textColor={
        VARIANT_TEXT_COLOR_MAP[variant as keyof typeof VARIANT_TEXT_COLOR_MAP]
      }
    >
      {title}
    </HeaderText>
    {status && (
      <BodyText size="xs" textColor="tertiary">
        {status}
      </BodyText>
    )}
  </div>
);
CalloutTitle.displayName = "CalloutTitle";

interface CalloutDescriptionProps {
  description: string;
}

const CalloutDescription: React.FC<CalloutDescriptionProps> = ({
  description,
}) => <BodyText size="sm">{description}</BodyText>;
CalloutDescription.displayName = "CalloutDescription";

interface CalloutProps {
  title: string;
  description?: string;
  children?: React.ReactNode;
  status?: string;
  subtle?: boolean;
}

const VARIANT_ICON_MAP = {
  info: Info,
  danger: OctagonX,
  success: ShieldCheck,
  warning: TriangleAlert,
  ai: Sparkles,
};

const Callout = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> &
    CalloutProps &
    VariantProps<typeof calloutVariants>
>(
  (
    {
      className,
      variant = "info",
      subtle = false,
      title,
      description,
      children,
      status,
      ...props
    },
    ref,
  ) => {
    const Icon = VARIANT_ICON_MAP[variant as keyof typeof VARIANT_ICON_MAP];
    return (
      <CalloutRoot
        ref={ref}
        variant={variant}
        subtle={subtle}
        className={cn("h-fit leading-none", className)}
        {...props}
      >
        {Icon && <Icon size={16} />}
        <CalloutTitle
          variant={variant || "info"}
          title={title}
          status={status}
        />
        {description && <CalloutDescription description={description} />}
        {children}
      </CalloutRoot>
    );
  },
);
Callout.displayName = "Callout";

export { Callout, calloutVariants };
